import React, { useRef, useMemo, useState } from 'react';
import {
  GalleryCatalog,
  GalleryCardContent,
  Section,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupValue,
  SelectionGroupOption,
  ButtonWithGAClickButton,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { KitchenCollection } from 'modules/collections';
import { analyticsUtils } from 'utils/analytics-utils';
import { BATHROOM_ELEMENT_BLOCK } from '../../../bathroom-constants';
import { DownloadCatalog } from './download-catalog';
import { SELECTION_GROUP_LIST } from './new-bathroom-gallery-constants';
import { getImageList } from './new-bathroom-gallery-utils';
import {
  newBathroomGalleryCatalogGrid,
  newBathroomGallerySectionMoreBtn,
  newBathroomGallerySectionTabs,
} from './new-bathroom-gallery-styles';

const INITIAL_IMAGES_PER_PAGE = 5;
const IMAGES_PER_PAGE = 6;

export const NewBathroomGallery = () => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [lastRefIndex, setLastRefIndex] = useState<number>(0);
  const [imagePerPage, setImagePerPage] = useState<number>(INITIAL_IMAGES_PER_PAGE);
  const productRef = useRef<HTMLAnchorElement[]>([]);

  const collectionList = useMemo(() => getImageList(activeTab) as KitchenCollection[], [activeTab]);
  const availableCollectionList = collectionList.slice(0, imagePerPage);
  const productsCount = collectionList.length;

  const promotions = availableCollectionList.map((collection: KitchenCollection, index: number) => ({
    name: `${BATHROOM_ELEMENT_BLOCK} / proekty`,
    creative: collection.displayedName,
    position: (index + 1).toString(),
  }));

  usePromoViewEvent(productRef, `${BATHROOM_ELEMENT_BLOCK} / proekty`, promotions, lastRefIndex);

  const handleTabChange = (e: React.MouseEvent, value: SelectionGroupValue) => {
    if (value !== activeTab) {
      setImagePerPage(INITIAL_IMAGES_PER_PAGE);
      setActiveTab(value as string);
    }

    const activeOption: SelectionGroupOption | undefined = SELECTION_GROUP_LIST.find(
      (option: SelectionGroupOption) => option.value === value
    );
    if (activeOption) {
      analyticsUtils.clickButtonEvent(
        `${BATHROOM_ELEMENT_BLOCK}`,
        activeOption.text,
        'Выберите свой стиль'
      );
    }
  };

  const handleCardClick = (_: KitchenCollection, position: number) => {
    analyticsUtils.promoClickEvent(`${BATHROOM_ELEMENT_BLOCK} / proekty`, promotions[position]);
  }

  const handleShowMoreBtnClick = () => {
    setLastRefIndex(imagePerPage);
    setImagePerPage(imagePerPage + IMAGES_PER_PAGE);
  };

  return (
    <Section>
      <SectionSubTitle margin="s">
        Выберите свой стиль
      </SectionSubTitle>

      <SelectionGroup
        className={newBathroomGallerySectionTabs}
        active={activeTab}
        onChange={handleTabChange}
        optionList={SELECTION_GROUP_LIST}
        rounded
      />

      {availableCollectionList.length > 0 && (
        <GalleryCatalog
          catalog={availableCollectionList as KitchenCollection[]}
          cardRefCb={(el: HTMLAnchorElement | null) => {
            const position = el?.getAttribute('data-position');
            if (position && el) {
              productRef.current[+position] = el;
            }
          }}
          className={newBathroomGalleryCatalogGrid}
          downloadCatalog={<DownloadCatalog />}
          renderCardContent={({ displayedName }) => <GalleryCardContent displayedName={displayedName} />}
          onCardClick={handleCardClick}
        />
      )}

      {imagePerPage <= productsCount && (
        <ButtonWithGAClickButton
          elementBlock={BATHROOM_ELEMENT_BLOCK}
          elementName="Выберите свой стиль"
          elementText="Показать ещё"
          className={newBathroomGallerySectionMoreBtn}
          rounded
          onClick={handleShowMoreBtnClick}
          adaptive={false}
        >
          Показать ещё
        </ButtonWithGAClickButton>
      )}
    </Section>
  );
}
